import { useCallback } from 'react';
import type {
  EnvironmentKey,
  ConfigEntityDetail,
  ConfigEntityListEndpointResponse,
  ListEndpointParams,
} from '@web-config-app/core';
import { ConfigEntityAuthAction, isConfigEntity } from '@web-config-app/core';
import { buildConfigEntityAuthHeaders } from '@web-config-app/api';
import { useQueryClient } from '@tanstack/react-query';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { getEndpointParamsWithValues } from '../../utilities/get-endpoint-params-with-values/get-endpoint-params-with-values';

export interface UseConfigEntityListQueryProps {
  /**
   * The environment to search for the config entity
   */
  environment: EnvironmentKey;
  /**
   * Optional auth header entity type override for config-entity list calls.
   * When omitted, the hook prefers the per-call domainEntityType filter and then
   * falls back to the current config entity's bound domain entity type if present.
   */
  domainEntityType?: string;
}

/**
 * Hook that provides an imperative function to query the config entity list endpoint.
 *
 * This hook returns a **callback function** (not state) that you call when you need to
 * fetch config entities on-demand. Unlike the declarative `useEntityList` hook, this does
 * not automatically fetch data or manage loading/error state.
 *
 * The returned function returns an array of entities (0 or more) depending on the query
 * parameters you provide. It uses React Query's `queryClient.fetchQuery` under the hood
 * for caching.
 *
 * @param props.environment - The environment to query against
 * @returns An async function that accepts query parameters and returns a Promise resolving
 * to an array of matching entities. Throws on error.
 *
 * @example
 * // Query entities by domain ID on-demand
 * ```typescript
 * const queryEntities = useConfigEntityListQuery({ environment: 'staging' });
 *
 * const handleSearch = async () => {
 *   try {
 *     const entities = await queryEntities({
 *       'filter[domainEntityID]': 'entity-123',
 *       'page[size]': '10'
 *     });
 *     console.log('Found:', entities); // array of entities
 *   } catch (error) {
 *     console.error('Query failed:', error);
 *   }
 * };
 * ```
 *
 * @see useEntityList - For declarative data fetching with automatic state management
 */

export const useConfigEntityListQuery = ({
  environment,
  domainEntityType,
}: UseConfigEntityListQueryProps) => {
  const { configEntity } = useConfigApp();
  const {
    endpoints: { list },
  } = configEntity;

  const currentEnvironmentFetch = useEnvironmentFetch(environment);
  const { endpointFetch } = getEntityEndpoint<ConfigEntityListEndpointResponse>(
    configEntity.endpoints.list,
    currentEnvironmentFetch,
  );
  const queryClient = useQueryClient();

  return useCallback(
    async (
      queryParamsArg: ListEndpointParams,
    ): Promise<ConfigEntityDetail[]> => {
      const configEntityDomainEntityType = isConfigEntity(configEntity)
        ? configEntity.domainEntity.type
        : undefined;
      const headerDomainEntityType =
        queryParamsArg['filter[domainEntityType]'] ??
        domainEntityType ??
        configEntityDomainEntityType;
      const queryParams = Object.entries(queryParamsArg)
        .filter(([, value]) => typeof value === 'string')
        .map(([name, value]) => ({ name, value }));
      const params = getEndpointParamsWithValues(queryParams);

      const queryKey = [
        environment,
        list.path,
        'list',
        headerDomainEntityType,
        ...Object.values(queryParams),
      ];

      const response = await queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          endpointFetch({
            params,
            headers: headerDomainEntityType
              ? buildConfigEntityAuthHeaders(
                  headerDomainEntityType,
                  ConfigEntityAuthAction.Get,
                )
              : undefined,
          }),
        staleTime: 0,
        retry: false,
      });

      return response?.data ?? [];
    },
    [
      configEntity,
      domainEntityType,
      environment,
      list.path,
      endpointFetch,
      queryClient,
    ],
  );
};
